import { ComponentIds } from '@inter-ikea-core/core';
import { ErrorMessages, PlannerEcsMultiSystem, ProductInfoComponent, ProductType } from '@inter-ikea-storageone/range';
/**
 * When adding cabinets, we must make room for it by deleting shelves
 * This system will check the snapActionCandidate and see if the frame is relevant
 * And if the frame has two adjecent shelves, and if so remove them to make room for the cabinet
 */
export class AddCabinetSystem extends PlannerEcsMultiSystem {
    constructor(coreApi) {
        super(coreApi, 'AddCabinetSystem', [
            ComponentIds.MOVED_TO_CANDIDATE_SNAP_POSITION_COMPONENT_ID,
            ComponentIds.IS_DRAGGED_COMPONENT_ID
        ]);
        this.shouldDisplayToastError = false;
        this.placementService = coreApi.placementService;
    }
    onAdd(_entity, newComponent) {
        if (newComponent.typeId === ComponentIds.MOVED_TO_CANDIDATE_SNAP_POSITION_COMPONENT_ID) {
            const snap = newComponent.snap;
            if (!snap) {
                return;
            }
            this.handleInteriorsBlockingCabinet(snap);
        }
        else {
            this.shouldDisplayToastError = true;
        }
    }
    onUpdate() { }
    onRemove(_entity, oldComponent) {
        if (oldComponent.typeId === ComponentIds.IS_DRAGGED_COMPONENT_ID) {
            this.shouldDisplayToastError = false;
        }
    }
    /**
     * Checks it the guest is desk or drawer and if it collides with other interiors
     * If so it moves these above, to make room for the new interior
     */
    handleInteriorsBlockingCabinet(snapActionCandidate) {
        const frame = snapActionCandidate.host;
        const interior = snapActionCandidate.guest;
        const productType = interior.getComponent(ProductInfoComponent.id)
            ?.productType;
        const prioritizedProductTypes = [ProductType.LADMAKARE_CABINET];
        const frameWidth = frame.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size?.width;
        const validFrame = frameWidth === 800;
        // Only validate for prioritized product types, i.e. cabinet, which should move shelves if needed to fit
        if (!productType || !prioritizedProductTypes.includes(productType) || !validFrame) {
            return;
        }
        const hostConnector = snapActionCandidate.hostConnector;
        const key = hostConnector.key;
        const prevKey = `hole-series-left-${hostConnector.hole - 1}`;
        const potentialKeys = [key, prevKey];
        const connections = frame.getComponent(ComponentIds.HOST_CONNECTIONS_COMPONENT_ID)?.connections;
        const cabinetHasCompartment = interior
            .getComponent(ComponentIds.HOST_CONNECTIONS_COMPONENT_ID)
            ?.connections[0].guestConnectorEntity?.getComponent(ProductInfoComponent.id)?.productType === ProductType.LADMAKARE_COMPARTMENT;
        const potentialConnectionsData = connections?.filter(con => potentialKeys.includes(con.hostConnector.key) &&
            (!cabinetHasCompartment ||
                (cabinetHasCompartment &&
                    Number(con.hostConnector.key[con.hostConnector.key.length - 1]) < 4)));
        const blockingEntities = potentialConnectionsData?.map(connection => connection.guestConnectorEntity);
        const shelvesToDelete = blockingEntities?.filter(entity => this.coreApi.entities.isProductType(entity, ProductType.LADMAKARE_SHELF));
        const blockedByOtherCabinet = blockingEntities?.some(e => e.getComponent(ProductInfoComponent.id)?.productType ===
            ProductType.LADMAKARE_CABINET && e.id !== interior.id);
        if (this.shouldDisplayToastError && blockedByOtherCabinet) {
            this.coreApi.emit.onPlacementAttempt(ErrorMessages.LadmakareCabinetBlockedByOtherCabinet);
            this.shouldDisplayToastError = false;
        }
        if (shelvesToDelete?.length === 2) {
            // delete both
            shelvesToDelete?.forEach(entity => {
                this.coreApi.entities.delete(entity);
            });
        }
        else if (shelvesToDelete?.length === 1) {
            // If shelvesToDelete === 1, we must check the other potential connection
            // If it is used by the same entity we try to move, we can delete the shelf
            // Thus making room to move the cabinet
            const potentialConnectionUsedByCurrentEntity = potentialKeys?.find(key => {
                // An edge case, do not delete shelf on hole-series-3 to make room for a cabinet to be placed on top position
                // I.e. hole-series-3 and 4, if cabinet has an compartment
                const topPositionBlocked = key.includes('-3') && cabinetHasCompartment;
                if (topPositionBlocked)
                    return false;
                const connection = connections?.find(c => {
                    return c.hostConnector.key === key;
                });
                const isCurrentEntity = connection?.guestConnectorEntity?.id === interior.id;
                if (isCurrentEntity)
                    return true;
                const keyHole = Number(key[key.length - 1]);
                const nextKeyHole = `hole-series-left-${keyHole + 1}`;
                const nextKeyGuestConnector = connections?.find(c => {
                    return c.hostConnector.key === nextKeyHole;
                })?.guestConnectorEntity;
                const isNextEntity = nextKeyGuestConnector?.id === interior.id;
                return isNextEntity;
            });
            if (potentialConnectionUsedByCurrentEntity) {
                this.coreApi.entities.delete(shelvesToDelete[0]);
            }
        }
    }
}
//# sourceMappingURL=AddCabinetSystem.js.map